/**
 * Pure TypeScript greedy EV charging optimizer.
 *
 * Given a session and a 15-min DA price series, it schedules charging into
 * the cheapest available slots within the plug-in window.
 */

import type { PricePoint } from '@/services/api/priceApi'
import type { components } from '../../../../types/flex-api'

type ChargingSession = components['schemas']['ChargingSession']

export interface SessionInput {
  /** Wh – battery capacity */
  maxEnergy: number
  /** % – SoC when plugged in */
  pluggedSoC: number
  /** % – target SoC (desiredSoC if available, else unpluggedSoC) */
  targetSoC: number
  /** W – maximum charging power */
  maxChargePower: number
  /** ISO string – when EV was plugged in */
  pluggedAt: string
  /** ISO string – charging window end (scheduledDepartureTime or unpluggedAt) */
  windowEnd: string
}

export interface SlotResult {
  timestampMs: number
  priceEurKwh: number
  /** kWh charged in this slot (0 if not selected) */
  energyKwh: number
  selected: boolean
}

export interface OptimizationResult {
  energyKwh: number
  flatCost: number
  optimalCost: number
  savings: number
  savingsPct: number
  schedule: SlotResult[]
}

export interface TradingDeskResult {
  baselineCost: number
  optimalCost: number
  tdSavings: number
  tdSavingsPct: number
}

const SLOT_DURATION_H = 0.25 // 15 minutes in hours
const SLOT_MS = 15 * 60 * 1000

export function optimizeSession(
  session: SessionInput,
  prices: PricePoint[],
  flatRate: number,
  offset: number,
  efficiency: number
): OptimizationResult {
  const windowStart = new Date(session.pluggedAt).getTime()
  const windowEnd = new Date(session.windowEnd).getTime()

  // Energy target in kWh
  const energyNeededKwh =
    ((session.maxEnergy / 1000) * Math.max(0, session.targetSoC - session.pluggedSoC)) / 100

  if (energyNeededKwh <= 0) {
    return {
      energyKwh: 0,
      flatCost: 0,
      optimalCost: 0,
      savings: 0,
      savingsPct: 0,
      schedule: [],
    }
  }

  const maxPowerKw = session.maxChargePower / 1000
  const maxEnergyPerSlotKwh = maxPowerKw * SLOT_DURATION_H * efficiency

  // Filter price slots within the charging window
  const availableSlots: SlotResult[] = prices
    .filter((p) => p.timestampMs >= windowStart && p.timestampMs < windowEnd)
    .map((p) => ({
      timestampMs: p.timestampMs,
      priceEurKwh: p.priceEurKwh,
      energyKwh: 0,
      selected: false,
    }))

  if (availableSlots.length === 0) {
    const flatCost = (energyNeededKwh * (flatRate + offset))
    return {
      energyKwh: energyNeededKwh,
      flatCost,
      optimalCost: flatCost,
      savings: 0,
      savingsPct: 0,
      schedule: [],
    }
  }

  // Greedy: fill cheapest slots first
  const sortedIndices = availableSlots
    .map((_, i) => i)
    .sort(
      (a, b) =>
        availableSlots[a].priceEurKwh + offset - (availableSlots[b].priceEurKwh + offset)
    )

  let remaining = energyNeededKwh
  let optimalCost = 0

  for (const idx of sortedIndices) {
    if (remaining <= 0) break
    const slot = availableSlots[idx]
    const charged = Math.min(maxEnergyPerSlotKwh, remaining)
    slot.energyKwh = charged
    slot.selected = true
    optimalCost += charged * (slot.priceEurKwh + offset)
    remaining -= charged
  }

  const actualEnergy = energyNeededKwh - Math.max(0, remaining)
  const flatCost = actualEnergy * (flatRate + offset)
  const savings = flatCost - optimalCost
  const savingsPct = flatCost > 0 ? (savings / flatCost) * 100 : 0

  // Sort schedule back to chronological order
  availableSlots.sort((a, b) => a.timestampMs - b.timestampMs)

  return {
    energyKwh: actualEnergy,
    flatCost,
    optimalCost,
    savings,
    savingsPct,
    schedule: availableSlots,
  }
}

/**
 * Compute the baseline cost for the Trading Desk perspective:
 * charge fills slots chronologically (first-available = "right after plug-in"),
 * using raw market prices (DA or IDA) only — no grid fees, taxes, or levies,
 * since those are end-customer charges, not wholesale trading costs.
 *
 * Returns the baseline cost alongside the already-computed optimal cost so the
 * caller can derive TD savings = baseline - optimal.
 */
export function computeTradingDeskBaseline(
  session: SessionInput,
  prices: PricePoint[],
  efficiency: number,
  optimalCost: number,
  energyKwh: number
): TradingDeskResult {
  if (energyKwh <= 0) {
    return { baselineCost: 0, optimalCost: 0, tdSavings: 0, tdSavingsPct: 0 }
  }

  const windowStart = new Date(session.pluggedAt).getTime()
  const windowEnd = new Date(session.windowEnd).getTime()

  const availableSlots = prices
    .filter((p) => p.timestampMs >= windowStart && p.timestampMs < windowEnd)
    .sort((a, b) => a.timestampMs - b.timestampMs)

  const maxPowerKw = session.maxChargePower / 1000
  const maxEnergyPerSlotKwh = maxPowerKw * SLOT_DURATION_H * efficiency

  let remaining = energyKwh
  let baselineCost = 0
  for (const slot of availableSlots) {
    if (remaining <= 0) break
    const charged = Math.min(maxEnergyPerSlotKwh, remaining)
    baselineCost += charged * slot.priceEurKwh
    remaining -= charged
  }

  const tdSavings = baselineCost - optimalCost
  const tdSavingsPct = baselineCost > 0 ? (tdSavings / baselineCost) * 100 : 0

  return { baselineCost, optimalCost, tdSavings, tdSavingsPct }
}

/**
 * Derive a Trading Desk scenario's savings against a shared baseline cost
 * (rather than a baseline computed on that scenario's own price curve). Used
 * so multiple scenarios can be ranked purely by optimal cost: whichever
 * scenario finds the cheapest slots always shows the highest savings.
 */
export function scenarioSavingsFromBaseline(baselineCost: number, optimalCost: number): TradingDeskResult {
  const tdSavings = baselineCost - optimalCost
  const tdSavingsPct = baselineCost > 0 ? (tdSavings / baselineCost) * 100 : 0
  return { baselineCost, optimalCost, tdSavings, tdSavingsPct }
}

/**
 * Blend two price series by taking the per-timestamp minimum, unioning the
 * timestamp sets. A timestamp present in only one series keeps that series'
 * price — this makes the blended series pointwise ≤ both inputs, so an
 * optimization against it never costs more than either input alone.
 */
export function blendMinPrices(a: PricePoint[], b: PricePoint[]): PricePoint[] {
  const map = new Map<number, number>()
  for (const p of a) map.set(p.timestampMs, p.priceEurKwh)
  for (const p of b) {
    const existing = map.get(p.timestampMs)
    map.set(p.timestampMs, existing === undefined ? p.priceEurKwh : Math.min(existing, p.priceEurKwh))
  }
  return Array.from(map.entries())
    .map(([timestampMs, priceEurKwh]) => ({ timestampMs, priceEurKwh }))
    .sort((x, y) => x.timestampMs - y.timestampMs)
}

export interface SlotDensity {
  kWh: number
  count: number
}

/**
 * Distribute chargingPhases energy into 15-min slot buckets.
 * Each phase's energyWh is pro-rated by the fraction of the phase that falls
 * within each slot. Sessions with no chargingPhases are skipped.
 */
export function buildActualSlotDensity(
  sessions: ChargingSession[]
): Map<number, SlotDensity> {
  const map = new Map<number, SlotDensity>()
  for (const session of sessions) {
    if (!session.chargingPhases || session.chargingPhases.length === 0) continue
    for (const phase of session.chargingPhases) {
      const phaseStart = new Date(phase.startedAt).getTime()
      const phaseEnd = new Date(phase.endedAt).getTime()
      const phaseDuration = phaseEnd - phaseStart
      if (phaseDuration <= 0 || phase.energyWh <= 0) continue

      const firstSlot = Math.floor(phaseStart / SLOT_MS) * SLOT_MS
      const lastSlot = Math.floor((phaseEnd - 1) / SLOT_MS) * SLOT_MS

      for (let slotMs = firstSlot; slotMs <= lastSlot; slotMs += SLOT_MS) {
        const overlapStart = Math.max(phaseStart, slotMs)
        const overlapEnd = Math.min(phaseEnd, slotMs + SLOT_MS)
        const overlap = overlapEnd - overlapStart
        if (overlap <= 0) continue
        const fraction = overlap / phaseDuration
        const slotKwh = (phase.energyWh / 1000) * fraction
        const existing = map.get(slotMs)
        if (existing) {
          existing.kWh += slotKwh
          // Only count a new session contribution once per slot
        } else {
          map.set(slotMs, { kWh: slotKwh, count: 1 })
        }
      }
    }
  }
  return map
}
